import type { ClientMessage } from "@numbet/protocol";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { fitsDecimals, formatWhileTyping, formatWithUnit, isYearUnit, parseGermanNumber } from "../format.ts";
import { t } from "../i18n.ts";

interface Props {
  unit: string;
  decimals: number;
  send: (message: ClientMessage) => void;
}

export function EstimateInput({ unit, decimals, send }: Props) {
  const isYear = isYearUnit(unit);
  const [text, setText] = useState("");
  const [beforeChrist, setBeforeChrist] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const parsed = parseGermanNumber(text);
  const value = parsed === null ? null : isYear && beforeChrist ? -Math.abs(parsed) : parsed;
  const valid = value !== null && fitsDecimals(value, decimals);

  useEffect(() => inputRef.current?.focus(), []);

  // Entwurf an den Server schicken: wird bei Ablauf der Zeit automatisch abgegeben (E-21)
  useEffect(() => {
    if (!valid || value === null) return;
    const timer = setTimeout(() => send({ type: "draftEstimate", value }), 400);
    return () => clearTimeout(timer);
  }, [valid, value, send]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (valid && value !== null) send({ type: "submitEstimate", value });
  };

  let preview = "";
  if (text.trim() !== "" && text !== "-") {
    if (value === null) preview = t.estimateInvalid;
    else if (!valid) preview = t.estimateDecimals(decimals);
    else preview = `= ${formatWithUnit(value, unit, decimals)}`;
  }

  return (
    <form className="estimate-form" onSubmit={onSubmit}>
      <label className="label" htmlFor="estimate">
        {t.yourEstimate} ({unit})
      </label>
      <div className="estimate-row">
        <input
          ref={inputRef}
          id="estimate"
          className="input estimate-input"
          inputMode={decimals > 0 ? "decimal" : "numeric"}
          autoComplete="off"
          enterKeyHint="send"
          value={text}
          placeholder="?"
          aria-describedby="estimate-preview"
          onChange={(e) => setText(formatWhileTyping(e.target.value, !isYear))}
        />
        {isYear && (
          <button
            type="button"
            className={`btn${beforeChrist ? " btn-primary" : ""}`}
            aria-pressed={beforeChrist}
            onClick={() => setBeforeChrist((v) => !v)}
          >
            {t.beforeChrist}
          </button>
        )}
      </div>
      <div id="estimate-preview" className="estimate-preview" aria-live="polite">
        {preview}
      </div>
      <button className="btn btn-primary btn-block btn-large" disabled={!valid}>
        {t.submitEstimate}
      </button>
      <div className="muted small" style={{ textAlign: "center" }}>
        {t.estimateFinal}
      </div>
    </form>
  );
}
